import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { TrashIcon } from "lucide-react";
import { ClassNameValue } from "tailwind-merge";
import { Button } from "../Base/Button";
import { FileUpload } from "../Common/FileUpload";
import { logo } from "@/repositories/logo";
import { EditSaveButtons } from "../Common/EditSaveButtons";
import shopHeroLogo from "../../assets/shop-hero-logo-horizontal.svg";

interface LogoProps {
  logoUrl?: string;
  shopId: string;
  canBeChanged?: boolean;
  className?: ClassNameValue;
  logoClassName?: ClassNameValue;
}

export const Logo = ({
  logoUrl,
  shopId,
  canBeChanged,
  className,
  logoClassName,
}: LogoProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [futureLogo, setFutureLogo] = useState<File>();
  const [currentLogoUrl, setCurrentLogoUrl] = useState(logoUrl);

  // The store resolves logoUrl asynchronously, so the prop is undefined on the
  // first render for every shop; without this the placeholder would stick.
  useEffect(() => setCurrentLogoUrl(logoUrl), [logoUrl]);

  const handleEditClick = () => setIsEditing(true);

  const handleSaveClick = () => {
    setIsEditing(false);

    if (!futureLogo) return;
    logo.upload(shopId, futureLogo);
    setCurrentLogoUrl(URL.createObjectURL(futureLogo)); // Update the current logo with the new one
    setFutureLogo(undefined);
  };

  const handleFileDropped = (file: File) => {
    setFutureLogo(file);
  };

  const handleDiscardClick = () => {
    setFutureLogo(undefined);
    setIsEditing(false);
  };

  const handleDeleteClick = () => {
    logo.delete(shopId); // Assuming a delete method exists in your logo model
    setCurrentLogoUrl(undefined); // Clear the current logo
    setIsEditing(true);
  };

  return (
    <div className={cn("relative", className)}>
      {currentLogoUrl && !isEditing && (
        <img
          src={currentLogoUrl}
          onError={() => setCurrentLogoUrl(undefined)}
          className={cn(
            "m-auto h-48 w-96 rounded-lg bg-white object-contain",
            logoClassName
          )}
          alt="Logo"
        />
      )}
      {!currentLogoUrl && !isEditing && (
        // A shop with no uploaded logo used to render nothing here, leaving an
        // empty "Shop Logo:" row. The wordmark stands in, muted and padded so
        // it reads as a placeholder rather than as the shop's own logo.
        <img
          src={shopHeroLogo}
          className={cn(
            "m-auto h-48 w-96 rounded-lg bg-white object-contain p-8 opacity-50",
            logoClassName
          )}
          alt="Default logo"
        />
      )}
      {isEditing && (
        <div className="m-auto h-48 w-96 rounded-lg">
          {futureLogo ? (
            <img
              src={URL.createObjectURL(futureLogo)}
              className={cn(
                "h-48 w-full bg-white object-contain",
                logoClassName
              )}
              alt="Logo"
            />
          ) : currentLogoUrl ? (
            <img
              src={currentLogoUrl}
              className={cn(
                "h-48 w-full bg-white object-contain",
                logoClassName
              )}
              alt="Logo"
            />
          ) : (
            <FileUpload
              text="Upload or click to browse to new logo"
              handleFileUpload={handleFileDropped}
            />
          )}
        </div>
      )}
      {canBeChanged && (
        <div
          className={cn(
            (currentLogoUrl || isEditing) && "absolute right-2 top-2",
            "flex gap-2"
          )}
        >
          {isEditing && currentLogoUrl && (
            <Button
              variant="destructive"
              size="icon"
              onClick={handleDeleteClick}
              className="rounded-full"
            >
              <TrashIcon className="h-5 w-5" />
            </Button>
          )}
          <EditSaveButtons
            isEditing={isEditing}
            handleDiscard={handleDiscardClick}
            handleEdit={handleEditClick}
            handleSave={handleSaveClick}
            saveDisabled={!futureLogo}
          />
        </div>
      )}
    </div>
  );
};
